'use client';

import React, { useRef, useEffect, useState } from 'react';
import { Gantt, StringHelper } from '@bryntum/gantt';
import '@bryntum/gantt/gantt.stockholm.css';

const GanttByProjectManager = ({ projects }) => {
  const ganttRef = useRef(null);
  const ganttInstance = useRef(null);
  const [tasks, setTasks] = useState([]);

  const formatDate = (date) => {
    try {
      if (date === '') return '';
      return (
        date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2)
      );
    } catch {
      return date;
    }
  };

  const calculateLeafBudgetPercent = (taskData) => {
    if (!taskData.budgetCost) return 0;
    return Math.round((taskData.efc / taskData.budgetCost) * 100);
  };

  const calculateParentProgress = (leafTasks) => {
    let efc = 0,
      budgetCost = 0,
      overallComplete = 0;
    leafTasks.forEach((leaf) => {
      efc += leaf.efc ?? 0;
      budgetCost += leaf.budgetCost ?? 0;
      overallComplete += leaf.overallComplete ?? 0;
    });
    return {
      budgetPercent: budgetCost ? Math.round((efc / budgetCost) * 100) : 0,
      parentProgress: leafTasks.length ? Math.round(overallComplete / leafTasks.length) : 0,
      efc,
      budgetCost,
    };
  };

  const calculateDuration = (start, end) => {
    return Math.max(1, Math.round((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)));
  };

  const transformData = (projects) => {
    const binned = {};

    projects.forEach((project) => {
      let pep = project.pepApprovalDate ? new Date(project.pepApprovalDate) : null;
      let ready = project.baselineReadyForService ? new Date(project.baselineReadyForService) : null;
      const today = new Date();

      if (!pep && ready) pep = ready;
      else if (pep && !ready) ready = pep;
      else if (!pep && !ready) pep = ready = today;

      if (ready < pep) ready = pep;

      project.pepApprovalDate = formatDate(pep);
      project.baselineReadyForService = formatDate(ready);

      const manager = project.projectManager || 'Not Specified';
      if (!binned[manager]) binned[manager] = [];
      binned[manager].push(project);
    });

    const result = [];
    let taskId = 1;

    Object.entries(binned)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([manager, groupedProjects]) => {
        groupedProjects.sort((a, b) => (a.afeNumber || '').localeCompare(b.afeNumber || ''));

        const leafTasks = groupedProjects.map((proj) => {
          const start = proj.pepApprovalDate;
          const end = proj.baselineReadyForService;
          return {
            id: taskId++,
            name: proj.name,
            netSuiteId: proj.netSuiteId,
            startDate: start,
            start,
            end,
            eventColor: 'purple',
            duration: calculateDuration(start, end),
            budgetUsed: calculateLeafBudgetPercent(proj),
            projectManager: proj.projectManager || '',
            manuallyScheduled: true,
            afeNumber: proj.afeNumber || '',
            description: proj.description || '',
            efc: proj.efc || 0,
            budgetCost: proj.budgetCost || 0,
            overallComplete: proj.overallComplete || 0,
            baselineReadyForService: end,
            type: proj.type || '',
            unit: proj.unit || '',
          };
        });

        const progress = calculateParentProgress(leafTasks);
        const parentStart = leafTasks[0].start;
        const parentEnd = leafTasks.reduce((max, t) => (t.end > max ? t.end : max), leafTasks[0].end);

        result.push({
          id: taskId++,
          name: manager,
          startDate: parentStart,
          start: parentStart,
          end: parentEnd,
          expanded: false,
          children: leafTasks,
          eventColor: 'purple',
          projectManager: 'Expand For Items',
          description: 'Project Manager Group',
          budgetUsed: progress.budgetPercent,
          efc: progress.efc,
          budgetCost: progress.budgetCost,
          overallComplete: progress.parentProgress,
          afeNumber: 'N/A',
          type: 'N/A',
          unit: 'N/A',
        });
      });

    return result;
  };

  useEffect(() => {
    if (projects?.length) {
      setTasks(transformData(projects));
    }
  }, [projects]);

  useEffect(() => {
    if (tasks.length && ganttRef.current) {
      ganttInstance.current?.destroy();

      ganttInstance.current = new Gantt({
        appendTo: ganttRef.current,
        rowHeight: 55,
        project: { tasksData: tasks },
        viewPreset: 'monthAndYear',
        columns: [
          {
            type: 'name',
            width: 300,
            htmlEncode: false,
            expandIconCls: 'b-fa b-fa-angles-down',
            collapseIconCls: 'b-fa b-fa-angles-down',
            leafIconCls: false,
          },
          { field: 'startDate', type: 'date', text: 'Start Date', width: 125, align: 'center', format: 'YYYY-MM-DD' },
          { field: 'endDate', type: 'date', text: 'End Date', width: 125, align: 'center', format: 'YYYY-MM-DD' },
          {
            field: 'overallComplete',
            type: 'percentdone',
            text: 'Complete %',
            width: 125,
            mode: 'circle',
            align: 'center',
          },
          { field: 'budgetUsed', type: 'percentdone', text: 'Budget %', width: 125, mode: 'circle', align: 'center' },
        ],
        features: {
          taskTooltip: {
            template: ({ taskRecord }) => `
              <div style="color:black; font-size: 14px; padding: 10px;">
                <strong>${StringHelper.encodeHtml(taskRecord.name || '')}</strong><br/>
                AFE Number: ${StringHelper.encodeHtml(taskRecord.afeNumber || '')}<br/>
                Type: ${StringHelper.encodeHtml(taskRecord.type || '')}<br/>
                PEP Approval Date: ${
                  taskRecord.children?.length ? 'N/A' : StringHelper.encodeHtml(taskRecord.start || '')
                }<br/>
                Baseline Ready For Service Date: ${
                  taskRecord.children?.length ? 'N/A' : StringHelper.encodeHtml(taskRecord.end || '')
                }<br/>
                % Project Complete: ${StringHelper.encodeHtml(String(taskRecord.overallComplete || 0))}%<br/>
                % Budget Used: ${StringHelper.encodeHtml(String(taskRecord.budgetUsed || 0))}%<br/>
                Description: ${StringHelper.encodeHtml(taskRecord.description || '')}
              </div>
            `,
          },
          taskEdit: false,
          cellMenu: false,
          taskMenu: false,
          projectLines: false,
          parentArea: true,
          timeRanges: {
            showCurrentTimeLine: {
              name: `${new Date().toLocaleDateString()}`,
            },
            showHeaderElements: true,
          },
        },
        taskRenderer: ({ taskRecord, renderData }) => {
          renderData.children.push({
            html: `
              <div style="transform: translateY(-20%);">
                <strong><em>
                  <span style="font-size: large; color: black;">
                    ${StringHelper.encodeHtml(taskRecord.name || '')}
                  </span>
                </em></strong>
              </div>
            `,
            className: 'percentBar',
            style: {
              position: 'absolute',
              width: `${taskRecord.overallComplete || 0}%`,
              height: '100%',
              display: 'flex',
              backgroundColor: 'rgba(255, 255, 255, 0.50)',
              paddingLeft: '1em',
              alignItems: 'center',
            },
          });
        },
      });

      ganttInstance.current.on('projectRefresh', () => {
        ganttInstance.current.zoomToFit({
          leftMargin: 5,
          rightMargin: 5,
        });
      });

      return () => {
        ganttInstance.current?.destroy();
        ganttInstance.current = null;
      };
    }
  }, [tasks]);

  return <div ref={ganttRef} style={{ height: '70vh', width: '100%' }} />;
};

export default GanttByProjectManager;
